import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Slide,
  IconButton,
  Stack,
  Switch,
  FormControlLabel,
  Link as MuiLink,
  Paper,
  Divider,
  Chip,
  Tooltip,
  Fade
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CookieIcon from '@mui/icons-material/Cookie';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SecurityOutlinedIcon from '@mui/icons-material/SecurityOutlined';
import AnalyticsOutlinedIcon from '@mui/icons-material/AnalyticsOutlined';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// Modern color palette
const cookieColors = {
  primary: '#0288d1',       // Blue for primary actions
  secondary: '#00acc1',     // Teal for secondary elements
  background: '#ffffff',    // White background
  text: {
    primary: '#263238',     // Dark blue-gray for main text
    secondary: '#546e7a'    // Medium blue-gray for secondary text
  },
  success: '#4caf50',       // Green for success/enabled states
  border: '#eceff1',        // Light blue-gray for borders
  hover: '#e1f5fe',         // Light blue for hover states
  cardBg: '#f5f7f9'         // Light gray-blue for card backgrounds
};

const CookieConsent = () => {
  const [open, setOpen] = useState(false);
  const [showBanner, setShowBanner] = useState(true);
  const [preferences, setPreferences] = useState({
    necessary: true,
    analytics: false,
    marketing: false,
    personalization: false,
  });

  useEffect(() => {
    const consent = localStorage.getItem('cookieConsent');
    if (consent) {
      setShowBanner(false);
      setPreferences(JSON.parse(consent));
    }
  }, []);

  const handleClose = () => {
    setOpen(false);
  };

  const handleAcceptAll = () => {
    const allAccepted = {
      necessary: true,
      analytics: true,
      marketing: true,
      personalization: true,
    };
    localStorage.setItem('cookieConsent', JSON.stringify(allAccepted));
    setPreferences(allAccepted);
    setShowBanner(false);
    setOpen(false);
  };

  const handleSavePreferences = () => {
    localStorage.setItem('cookieConsent', JSON.stringify(preferences));
    setShowBanner(false);
    setOpen(false);
  };

  const handlePreferenceChange = (name) => (event) => {
    setPreferences({
      ...preferences,
      [name]: event.target.checked,
    });
  };

  if (!showBanner) return null;

  return (
    <>
      {/* Modern Cookie Banner */}
      <Fade in={showBanner}>
        <Paper
          elevation={6}
          sx={{
            position: 'fixed',
            bottom: 24,
            left: { xs: 16, md: 32 },
            right: { xs: 16, md: 'auto' },
            maxWidth: { xs: 'calc(100% - 32px)', md: 420 },
            borderRadius: 3,
            overflow: 'hidden',
            zIndex: 1300,
            display: showBanner ? 'block' : 'none',
            boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
            border: `1px solid ${cookieColors.border}`,
          }}
        >
          {/* Header */}
          <Box
            sx={{
              bgcolor: cookieColors.cardBg,
              p: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              borderBottom: `1px solid ${cookieColors.border}`,
            }}
          >
            <Box
              sx={{
                bgcolor: cookieColors.primary,
                borderRadius: '50%',
                width: 40,
                height: 40,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <CookieIcon sx={{ fontSize: 24, color: 'white' }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.1rem', color: cookieColors.text.primary }}>
                Cookie Preferences
              </Typography>
              <Typography variant="body2" sx={{ color: cookieColors.text.secondary, fontSize: '0.85rem' }}>
                Customize how we use cookies on this site
              </Typography>
            </Box>
          </Box>

          {/* Content */}
          <Box sx={{ p: 2.5 }}>
            <Typography variant="body2" sx={{ color: cookieColors.text.secondary, mb: 2.5 }}>
              We use cookies to enhance your browsing experience, serve personalized content, and analyze our traffic. By clicking "Accept All", you consent to our use of cookies.
            </Typography>
            
            <Stack direction="row" spacing={1.5} sx={{ mt: 3 }}>
              <Button
                variant="outlined"
                onClick={() => setOpen(true)}
                sx={{
                  flex: 1,
                  color: cookieColors.text.primary,
                  borderColor: cookieColors.border,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 500,
                  '&:hover': {
                    borderColor: cookieColors.primary,
                    bgcolor: cookieColors.hover,
                  },
                }}
              >
                Customize
              </Button>
              <Button
                variant="contained"
                onClick={handleAcceptAll}
                sx={{
                  flex: 1,
                  bgcolor: cookieColors.primary,
                  color: 'white',
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 500,
                  boxShadow: '0 4px 12px rgba(2, 136, 209, 0.2)',
                  '&:hover': {
                    bgcolor: '#0277bd',
                    boxShadow: '0 6px 16px rgba(2, 136, 209, 0.25)',
                  },
                }}
              >
                Accept All
              </Button>
            </Stack>
          </Box>
        </Paper>
      </Fade>

      {/* Modern Detailed Cookie Settings Dialog */}
      <Dialog
        open={open}
        onClose={handleClose}
        TransitionComponent={Transition}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: 'hidden',
          }
        }}
      >
        <DialogTitle 
          sx={{ 
            m: 0, 
            p: 3, 
            bgcolor: cookieColors.cardBg,
            borderBottom: `1px solid ${cookieColors.border}`,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <CookieIcon sx={{ color: cookieColors.primary, fontSize: 28 }} />
            <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.25rem' }}>
              Cookie Settings
            </Typography>
          </Box>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              position: 'absolute',
              right: 16,
              top: 16,
              color: cookieColors.text.secondary,
              '&:hover': {
                bgcolor: 'rgba(0,0,0,0.04)',
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 0 }}>
          {/* Intro section */}
          <Box sx={{ p: 3, pb: 2 }}>
            <Typography variant="body1" sx={{ mb: 1, color: cookieColors.text.primary }}>
              Manage your cookie preferences
            </Typography>
            <Typography variant="body2" sx={{ color: cookieColors.text.secondary }}>
              We use cookies to enhance your browsing experience, analyze site traffic, and personalize content. You can choose which cookies you allow us to use.
            </Typography>
            
            <Chip 
              label="Privacy First" 
              size="small" 
              icon={<SecurityOutlinedIcon sx={{ fontSize: 16 }} />}
              sx={{ 
                mt: 2, 
                bgcolor: 'rgba(76, 175, 80, 0.1)', 
                color: cookieColors.success,
                fontWeight: 500,
                '& .MuiChip-icon': { color: cookieColors.success }
              }} 
            />
          </Box>
          
          <Divider />
          
          {/* Cookie options */}
          <Box sx={{ p: 3 }}>
            {/* Necessary Cookies */}
            <Paper 
              elevation={0} 
              sx={{ 
                p: 2, 
                mb: 2, 
                borderRadius: 2, 
                bgcolor: cookieColors.cardBg,
                border: `1px solid ${cookieColors.border}`,
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                  <SecurityOutlinedIcon sx={{ color: cookieColors.success }} />
                  <Typography variant="subtitle1" fontWeight="600">
                    Necessary Cookies
                    <Tooltip title="These cookies are required for the website to function and cannot be disabled" placement="top">
                      <InfoOutlinedIcon sx={{ ml: 1, fontSize: 16, color: cookieColors.text.secondary, verticalAlign: 'text-top' }} />
                    </Tooltip>
                  </Typography>
                </Box>
                <Switch checked={preferences.necessary} disabled sx={{ '& .Mui-checked': { color: cookieColors.success } }} />
              </Box>
              <Typography variant="body2" sx={{ color: cookieColors.text.secondary, ml: 4.5 }}>
                Essential for the website to function properly. These cannot be disabled.
              </Typography>
            </Paper>
            
            {/* Analytics Cookies */}
            <Paper 
              elevation={0} 
              sx={{ 
                p: 2, 
                mb: 2, 
                borderRadius: 2, 
                bgcolor: preferences.analytics ? `${cookieColors.primary}08` : cookieColors.cardBg,
                border: `1px solid ${preferences.analytics ? cookieColors.primary : cookieColors.border}`,
                transition: 'all 0.2s ease',
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                  <AnalyticsOutlinedIcon sx={{ color: preferences.analytics ? cookieColors.primary : cookieColors.text.secondary }} />
                  <Typography variant="subtitle1" fontWeight="600">
                    Analytics Cookies
                  </Typography>
                </Box>
                <Switch 
                  checked={preferences.analytics} 
                  onChange={handlePreferenceChange('analytics')} 
                  sx={{ '& .Mui-checked': { color: cookieColors.primary } }}
                />
              </Box>
              <Typography variant="body2" sx={{ color: cookieColors.text.secondary, ml: 4.5 }}>
                Help us understand how visitors interact with our website and improve performance.
              </Typography>
            </Paper>
            
            {/* Marketing Cookies */}
            <Paper 
              elevation={0} 
              sx={{ 
                p: 2, 
                mb: 2, 
                borderRadius: 2, 
                bgcolor: preferences.marketing ? `${cookieColors.primary}08` : cookieColors.cardBg,
                border: `1px solid ${preferences.marketing ? cookieColors.primary : cookieColors.border}`,
                transition: 'all 0.2s ease',
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                  <CampaignOutlinedIcon sx={{ color: preferences.marketing ? cookieColors.primary : cookieColors.text.secondary }} />
                  <Typography variant="subtitle1" fontWeight="600">
                    Marketing Cookies
                  </Typography>
                </Box>
                <Switch 
                  checked={preferences.marketing} 
                  onChange={handlePreferenceChange('marketing')} 
                  sx={{ '& .Mui-checked': { color: cookieColors.primary } }}
                />
              </Box>
              <Typography variant="body2" sx={{ color: cookieColors.text.secondary, ml: 4.5 }}>
                Used to display relevant advertisements and track campaign performance.
              </Typography>
            </Paper>
            
            {/* Personalization Cookies */}
            <Paper 
              elevation={0} 
              sx={{ 
                p: 2, 
                borderRadius: 2, 
                bgcolor: preferences.personalization ? `${cookieColors.primary}08` : cookieColors.cardBg,
                border: `1px solid ${preferences.personalization ? cookieColors.primary : cookieColors.border}`,
                transition: 'all 0.2s ease',
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                  <PersonOutlineOutlinedIcon sx={{ color: preferences.personalization ? cookieColors.primary : cookieColors.text.secondary }} />
                  <Typography variant="subtitle1" fontWeight="600">
                    Personalization Cookies
                  </Typography>
                </Box>
                <Switch 
                  checked={preferences.personalization} 
                  onChange={handlePreferenceChange('personalization')} 
                  sx={{ '& .Mui-checked': { color: cookieColors.primary } }}
                />
              </Box>
              <Typography variant="body2" sx={{ color: cookieColors.text.secondary, ml: 4.5 }}>
                Remember your preferences and provide enhanced, personalized features.
              </Typography>
            </Paper>
          </Box>
          
          <Divider />
          
          <Box sx={{ p: 3, pt: 2 }}>
            <Typography variant="body2" sx={{ color: cookieColors.text.secondary }}>
              For more details on how we use cookies, please read our{' '}
              <MuiLink href="/privacy-policy" underline="hover" sx={{ color: cookieColors.primary, fontWeight: 500 }}>
                Privacy Policy
              </MuiLink>
            </Typography>
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ px: 3, py: 2.5, borderTop: `1px solid ${cookieColors.border}` }}>
          <Button 
            onClick={handleClose} 
            sx={{ 
              color: cookieColors.text.secondary,
              textTransform: 'none',
              fontWeight: 500,
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSavePreferences} 
            variant="contained" 
            sx={{ 
              bgcolor: cookieColors.primary,
              color: 'white',
              textTransform: 'none',
              fontWeight: 500,
              borderRadius: 2,
              px: 3,
              '&:hover': {
                bgcolor: '#0277bd',
              },
            }}
          >
            Save Preferences
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CookieConsent;
